/**
 * Выбор аудиоустройств и реакция на смену гарнитуры.
 *
 * Три правила из спеки §7.9, каждое проверено болью:
 * 1. enumerateDevices() до первого getUserMedia отдаёт устройства с пустыми labels —
 *    диспетчер видит «Устройство 1, Устройство 2» и выбирает наугад;
 * 2. вынули USB-гарнитуру в разговоре — devicechange, дорожку надо подменить,
 *    иначе микрофон молчит до конца звонка;
 * 3. выбор надо помнить между сменами: диспетчер не должен настраивать звук каждое утро.
 */
const STORAGE_KEY_INPUT = 'cc.softphone.audio.input';
const STORAGE_KEY_OUTPUT = 'cc.softphone.audio.output';
/**
 * Названия устройств храним рядом с идентификаторами, и это не дублирование.
 *
 * Идентификатор устройства в браузере привязан к сессии: он пересчитывается,
 * когда чистят данные сайта, иногда — при перезапуске браузера. Сохранённый
 * идентификатор после этого не совпадает ни с одним устройством в списке, и
 * выбор молча слетает на «системное по умолчанию». Диспетчер видит это как
 * «посмотрел ролик, вернулся — микрофон сбросился».
 *
 * Название устройства переживает пересчёт. Поэтому при восстановлении сначала
 * ищем по идентификатору, а если его больше нет — по названию.
 */
const STORAGE_KEY_INPUT_LABEL = 'cc.softphone.audio.input.label';
const STORAGE_KEY_OUTPUT_LABEL = 'cc.softphone.audio.output.label';
/** Пустая строка в хранилище означает «системное по умолчанию», а не выбранное устройство */
function readStored(storage, key) {
    const value = storage?.getItem(key);
    return value !== null && value !== undefined && value !== '' ? value : null;
}
export class AudioDeviceManager {
    #media;
    #storage;
    #deps;
    #selection = { inputId: null, outputId: null };
    #outputElement = null;
    #onDeviceChange = null;
    #primed = false;
    constructor(deps = {}) {
        this.#deps = deps;
        this.#media = deps.mediaDevices ?? null;
        this.#storage = deps.storage ?? null;
        this.#selection = {
            inputId: readStored(this.#storage, STORAGE_KEY_INPUT),
            outputId: readStored(this.#storage, STORAGE_KEY_OUTPUT),
        };
    }
    getSelection() {
        return this.#selection;
    }
    /**
     * Первый запрос доступа к микрофону. Без него список устройств бесполезен,
     * поэтому вызывается один раз при входе на пульт, а не перед первым звонком.
     */
    async prime() {
        if (this.#primed || !this.#media)
            return;
        const stream = await this.#media.getUserMedia({ audio: true });
        // Дорожка нужна была только ради выдачи прав — держать её открытой нельзя,
        // иначе в браузере всю смену горит индикатор записи.
        for (const track of stream.getTracks())
            track.stop();
        this.#primed = true;
    }
    async list() {
        if (!this.#media)
            return { inputs: [], outputs: [] };
        const devices = await this.#media.enumerateDevices();
        return {
            inputs: devices.filter((d) => d.kind === 'audioinput'),
            outputs: devices.filter((d) => d.kind === 'audiooutput'),
        };
    }
    /** Возвращает новую дорожку выбранного микрофона — её подставляют в активную сессию */
    async setInput(deviceId) {
        this.#selection = { ...this.#selection, inputId: deviceId };
        this.#storage?.setItem(STORAGE_KEY_INPUT, deviceId ?? '');
        await this.#rememberLabel('audioinput', deviceId, STORAGE_KEY_INPUT_LABEL);
        this.#deps.onSelectionChange?.(this.#selection);
        if (!this.#media)
            return null;
        const stream = await this.#media.getUserMedia({
            audio: deviceId !== null ? { deviceId: { exact: deviceId } } : true,
        });
        return stream.getAudioTracks()[0] ?? null;
    }
    /**
     * Выбранный микрофон ещё есть в списке? Нет — сбрасываем на системный, не
     * открывая микрофон (15.09.2026). Идентификатор гарнитуры меняется без
     * события devicechange (USB, Bluetooth), и звонок с несуществующим
     * устройством падает «Requested device not found».
     */
    async ensureInputAvailable() {
        const { inputId } = this.#selection;
        if (inputId === null || !this.#media)
            return inputId;
        try {
            const list = await this.list();
            // Пустые id — доступ к микрофону ещё не выдан, сверять не с чем
            if (list.inputs.every((d) => d.deviceId === ''))
                return inputId;
            if (list.inputs.some((d) => d.deviceId === inputId))
                return inputId;
        }
        catch {
            return inputId;
        }
        this.resetInput();
        return null;
    }
    /**
     * Микрофон — системный по умолчанию. Без getUserMedia: дорожку откроет сам
     * звонок. Название гарнитуры не стираем — restore() найдёт её по нему, когда
     * гарнитуру снова подключат.
     */
    resetInput() {
        this.#selection = { ...this.#selection, inputId: null };
        this.#storage?.setItem(STORAGE_KEY_INPUT, '');
        this.#deps.onSelectionChange?.(this.#selection);
    }
    async setOutput(deviceId) {
        this.#selection = { ...this.#selection, outputId: deviceId };
        this.#storage?.setItem(STORAGE_KEY_OUTPUT, deviceId ?? '');
        await this.#rememberLabel('audiooutput', deviceId, STORAGE_KEY_OUTPUT_LABEL);
        this.#deps.onSelectionChange?.(this.#selection);
        await this.#applySink();
    }
    /**
     * Возвращает выбор к сохранённому — вызывается после выдачи доступа к микрофону.
     *
     * До выдачи доступа список устройств приходит без названий, и сопоставлять
     * не с чем. Поэтому восстановление отдельным шагом, а не в конструкторе.
     */
    async restore() {
        if (!this.#media)
            return this.#selection;
        const list = await this.list();
        const inputId = this.#resolve(list.inputs, this.#selection.inputId, readStored(this.#storage, STORAGE_KEY_INPUT_LABEL));
        const outputId = this.#resolve(list.outputs, this.#selection.outputId, readStored(this.#storage, STORAGE_KEY_OUTPUT_LABEL));
        this.#selection = { inputId, outputId };
        // Идентификатор мог смениться — перезаписываем, иначе искать по названию
        // придётся каждый раз
        this.#storage?.setItem(STORAGE_KEY_INPUT, inputId ?? '');
        this.#storage?.setItem(STORAGE_KEY_OUTPUT, outputId ?? '');
        this.#deps.onSelectionChange?.(this.#selection);
        await this.#applySink();
        return this.#selection;
    }
    /** Идентификатор ещё живой — берём его; нет — ищем то же устройство по названию */
    #resolve(devices, storedId, storedLabel) {
        if (storedId !== null && devices.some((d) => d.deviceId === storedId))
            return storedId;
        if (storedLabel === null)
            return null;
        const byLabel = devices.find((d) => d.label === storedLabel);
        return byLabel?.deviceId ?? null;
    }
    async #rememberLabel(kind, deviceId, key) {
        if (deviceId === null) {
            this.#storage?.setItem(key, '');
            return;
        }
        if (!this.#media)
            return;
        const devices = await this.#media.enumerateDevices();
        const found = devices.find((d) => d.kind === kind && d.deviceId === deviceId);
        this.#storage?.setItem(key, found?.label ?? '');
    }
    /** Элемент со звуком станции живёт вне React-дерева и переживает ремаунт */
    attachOutputElement(element) {
        this.#outputElement = element;
        void this.#applySink();
    }
    start() {
        if (!this.#media || this.#onDeviceChange)
            return;
        this.#onDeviceChange = () => {
            void this.#handleDeviceChange();
        };
        this.#media.addEventListener('devicechange', this.#onDeviceChange);
    }
    stop() {
        if (!this.#media || !this.#onDeviceChange)
            return;
        this.#media.removeEventListener('devicechange', this.#onDeviceChange);
        this.#onDeviceChange = null;
    }
    /**
     * Гарнитуру воткнули или вынули. Если выбранного устройства больше нет —
     * молча откатываемся на системное, иначе звонок останется без звука.
     */
    async #handleDeviceChange() {
        const list = await this.list();
        this.#deps.onDevicesChanged?.(list);
        const { inputId, outputId } = this.#selection;
        if (inputId !== null && !list.inputs.some((d) => d.deviceId === inputId)) {
            await this.setInput(null);
        }
        if (outputId !== null && !list.outputs.some((d) => d.deviceId === outputId)) {
            await this.setOutput(null);
        }
    }
    async #applySink() {
        const element = this.#outputElement;
        const outputId = this.#selection.outputId;
        if (!element || outputId === null || typeof element.setSinkId !== 'function')
            return;
        await element.setSinkId(outputId);
    }
}
//# sourceMappingURL=audio-devices.js.map